import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import Header from '../../components/common/Header';
import Sidebar from '../../components/common/Sidebar';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { UserCheck, Plus, Edit, X, Image as ImageIcon, Sparkles, Building, Mail, CheckCircle2, ShieldAlert } from 'lucide-react';
import './AdminHR.css';

const AdminHR = () => {
  const [hrProfiles, setHrProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingHr, setEditingHr] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    company_name: 'Technaureus Info Solutions',
    designation: 'Chief HR Officer',
    email: '',
    greeting_message: 'Hi! I am your HR Officer. Welcome to MentorAI!',
    is_active: true
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  const { addToast } = useToast();

  useEffect(() => {
    fetchHRProfiles();
  }, []);

  const fetchHRProfiles = async () => {
    try {
      setLoading(true);
      const res = await api.get('/hr-profiles/');
      setHrProfiles(res.data.results || res.data);
    } catch (err) {
      console.error(err);
      addToast('Failed to load HR profiles.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (hr = null) => {
    if (hr) {
      setEditingHr(hr);
      setFormData({
        name: hr.name || '',
        company_name: hr.company_name || 'Technaureus Info Solutions',
        designation: hr.designation || 'Chief HR Officer',
        email: hr.email || '',
        greeting_message: hr.greeting_message || 'Hi! I am your HR Officer. Welcome to MentorAI!',
        is_active: hr.is_active !== undefined ? hr.is_active : true
      });
      setAvatarPreview(hr.avatar_url || hr.avatar || null);
    } else {
      setEditingHr(null);
      setFormData({
        name: '',
        company_name: 'Technaureus Info Solutions',
        designation: 'Chief HR Officer',
        email: '',
        greeting_message: 'Hi! I am your HR Officer. Welcome to MentorAI!',
        is_active: true
      });
      setAvatarPreview(null);
    }
    setAvatarFile(null);
    setShowModal(true);
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.company_name.trim()) {
      addToast('HR Name and Company Name are required.', 'warning');
      return;
    }

    try {
      setSaving(true);
      const submitData = new FormData();
      submitData.append('name', formData.name.trim());
      submitData.append('company_name', formData.company_name.trim());
      submitData.append('designation', formData.designation.trim());
      submitData.append('email', formData.email.trim());
      submitData.append('greeting_message', formData.greeting_message.trim());
      submitData.append('is_active', formData.is_active ? 'true' : 'false');

      if (avatarFile) {
        submitData.append('avatar', avatarFile);
      }

      if (editingHr) {
        await api.patch(`/hr-profiles/${editingHr.id}/`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        addToast('HR Profile updated successfully!', 'success');
      } else {
        await api.post('/hr-profiles/', submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        addToast('HR Profile created successfully!', 'success');
      }

      setShowModal(false);
      fetchHRProfiles();
    } catch (err) {
      console.error(err);
      addToast(err.response?.data?.message || 'Failed to save HR Profile.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const activeHr = hrProfiles.find(h => h.is_active) || hrProfiles[0];

  return (
    <div className="page-layout-root">
      <Header />
      <div className="page-body-wrapper">
        <Sidebar />
        <main className="page-main-content">
          
          <div className="admin-page-header">
            <div>
              <h1 className="admin-page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <UserCheck style={{ color: '#4f46e5' }} /> HR Persona Management
              </h1>
              <p className="admin-page-subtitle">
                Manage HR profiles and configure the active HR Officer persona displayed across Policy Chat, Onboarding Hub, and Company Policies.
              </p>
            </div>
            <button className="btn btn-primary" onClick={() => handleOpenModal(null)}>
              <Plus size={18} /> Add HR Profile
            </button>
          </div>

          {activeHr && (
            <div className="admin-hr-active-card card">
              <div className="active-badge-tag"><Sparkles size={14} /> Currently Active Portal HR Persona</div>
              <div className="active-hr-flex">
                <div className="active-hr-avatar-wrapper">
                  {activeHr.avatar_url || activeHr.avatar ? (
                    <img src={activeHr.avatar_url || activeHr.avatar} alt={activeHr.name} className="active-hr-img" />
                  ) : (
                    <div className="active-hr-avatar-fallback">{activeHr.name ? activeHr.name.charAt(0).toUpperCase() : 'H'}</div>
                  )}
                  <span className="online-indicator" title="Active HR Online"></span>
                </div>
                <div className="active-hr-info">
                  <h3>{activeHr.name}</h3>
                  <div className="hr-meta-tags">
                    <span className="meta-pill"><Building size={14} /> {activeHr.company_name}</span>
                    <span className="meta-pill designation-pill">{activeHr.designation}</span>
                    {activeHr.email && <span className="meta-pill"><Mail size={14} /> {activeHr.email}</span>}
                  </div>
                  <div className="greeting-preview">
                    <strong>Portal Welcome Message:</strong> "{activeHr.greeting_message}"
                  </div>
                </div>
                <div className="active-hr-actions">
                  <button className="btn btn-secondary" onClick={() => handleOpenModal(activeHr)}>
                    <Edit size={16} /> Edit HR Details
                  </button>
                </div>
              </div>
            </div>
          )}

          <div style={{ margin: '24px 0 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-main, #f8fafc)' }}>All Registered HR Profiles ({hrProfiles.length})</h2>
          </div>

          {loading ? (
            <LoadingSpinner label="Loading HR Profiles..." />
          ) : (
            <div className="admin-hr-grid">
              {hrProfiles.map((hr) => (
                <div key={hr.id} className={`card hr-card ${hr.is_active ? 'is-active-card' : ''}`}>
                  <div className="hr-card-top">
                    <div className="hr-avatar-box">
                      {hr.avatar_url || hr.avatar ? (
                        <img src={hr.avatar_url || hr.avatar} alt={hr.name} className="hr-card-img" />
                      ) : (
                        <div className="hr-card-fallback">{hr.name.charAt(0).toUpperCase()}</div>
                      )}
                    </div>
                    <div className="hr-card-titles">
                      <h4>{hr.name}</h4>
                      <span className="hr-company-tag">{hr.company_name}</span>
                      <span className="hr-desig-tag">{hr.designation}</span>
                    </div>
                  </div>

                  <div className="hr-card-body">
                    {hr.email && <p className="hr-email"><Mail size={14} /> {hr.email}</p>}
                    <p className="hr-greeting-snippet">"{hr.greeting_message}"</p>
                  </div>

                  <div className="hr-card-footer">
                    <span className={`status-tag ${hr.is_active ? 'status-active' : 'status-inactive'}`}>
                      {hr.is_active ? <><CheckCircle2 size={14} /> Active Persona</> : <><ShieldAlert size={14} /> Inactive</>}
                    </span>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleOpenModal(hr)} title="Edit HR Profile">
                      <Edit size={14} /> Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {showModal && (
        <div className="modal-backdrop">
          <div className="modal-content" style={{ padding: '32px', maxWidth: '640px', width: '90%' }}>
            <div className="admin-modal-header">
              <h2 className="admin-modal-title">{editingHr ? 'Edit HR Profile' : 'Add HR Profile'}</h2>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>

            <form onSubmit={handleSubmit} className="admin-hr-form">
              <div className="form-row">
                <div className="form-group flex-1">
                  <label className="form-label">Full Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    required
                    placeholder="e.g. Sarah Jenkins"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="form-group flex-1">
                  <label className="form-label">Company Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    required
                    placeholder="e.g. Technaureus Info Solutions"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group flex-1">
                  <label className="form-label">Designation</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. Chief HR Officer"
                    value={formData.designation}
                    onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                  />
                </div>
                <div className="form-group flex-1">
                  <label className="form-label">Email Address</label>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="e.g. hr@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Portal Welcome / Greeting Message</label>
                <textarea
                  rows="3"
                  className="form-textarea"
                  placeholder="Welcome message displayed on policy chat and onboarding pages..."
                  value={formData.greeting_message}
                  onChange={(e) => setFormData({ ...formData, greeting_message: e.target.value })}
                ></textarea>
              </div>

              <div className="form-group">
                <label className="form-label">HR Avatar Photo (Full Size Display)</label>
                <div className="avatar-upload-box">
                  {avatarPreview ? (
                    <div className="avatar-upload-preview-container">
                      <img src={avatarPreview} alt="Avatar Preview" className="avatar-upload-preview" />
                    </div>
                  ) : (
                    <div className="avatar-upload-placeholder"><ImageIcon size={24} /></div>
                  )}
                  <input type="file" accept="image/*" onChange={handleAvatarChange} className="form-input file-input" />
                </div>
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Set as Active HR Persona for Portal
                </label>
              </div>

              <div className="admin-modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editingHr ? 'Update HR Profile' : 'Create HR Profile'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminHR;
